import { useEffect, useRef, useState } from "react";
import { ESwapBalances, SwapOrderMode } from "./mmTypes";
import { useAlertService } from "../../hooks/useAlertService";

const connectionUrl = (id: string) => `https://lukkypro.ru/api/v1/connections/${id}`;

async function request(url: string, options: RequestInit = {}) {
  const response = await fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${localStorage.getItem("authToken")}`,
      "Content-Type": "application/json",
    },
  });
  const body = await response.json();
  if (!response.ok) {
    throw new Error(body.message ||
      (typeof body.detail === "string" ? body.detail : "Не удалось выполнить запрос"));
  }
  return body;
}

interface Props { visState: boolean; connectionId: string; }

export const SwapBalances: React.FC<Props> = ({ visState, connectionId }) => {
  const { showAlert } = useAlertService();
  const [fromBot, setFromBot] = useState<ESwapBalances>(ESwapBalances.MM);
  const toBot = fromBot === ESwapBalances.MM ? ESwapBalances.LIQUID : ESwapBalances.MM;
  const [orderMode, setOrderMode] = useState<SwapOrderMode>("SELL_BUY");
  // Serialize decimal inputs as strings to preserve precision.
  const [price, setPrice] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [message, setMessage] = useState("");
  const inFlight = useRef(false);

  useEffect(() => {
    if (!visState) return;
    const controller = new AbortController();
    setLoaded(false);
    setMessage("");
    request(connectionUrl(connectionId), { signal: controller.signal })
      .then((connection) => {
        if (!controller.signal.aborted) {
          setOrderMode(connection.swap_order_mode || "SELL_BUY");
          setLoaded(true);
        }
      })
      .catch((error) => {
        if (!controller.signal.aborted) setMessage(error.message);
      });
    return () => controller.abort();
  }, [connectionId, visState]);

  const saveMode = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    try {
      await request(`${connectionUrl(connectionId)}/swap_settings`, {
        method: "PATCH", body: JSON.stringify({ order_mode: orderMode }),
      });
      setMessage("Порядок свапа сохранён");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Ошибка сохранения");
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  const execute = async (event: React.FormEvent) => {
    event.preventDefault();
    if (inFlight.current || !loaded) return;
    inFlight.current = true;
    setBusy(true);
    setMessage("");
    try {
      const result = await request(`${connectionUrl(connectionId)}/transfer_balances`, {
        method: "POST",
        body: JSON.stringify({ from_bot: fromBot, to_bot: toBot, price, amount, order_mode: orderMode }),
      });
      const direction = result.executed_order_mode === "BUY_SELL" ? "BUY → SELL" : "SELL → BUY";
      const gap = typeof result.dispatch_gap_ms === "number"
        ? ` Интервал отправки: ${result.dispatch_gap_ms.toFixed(0)} мс.` : "";
      setMessage(`Ордера отправлены: ${direction}. ID: ${(result.order_ids || []).join(", ")}.${gap} Проверьте исполнение и балансы обоих счетов.`);
      showAlert({ type: "success", message: "Оба ордера свапа отправлены" });
    } catch (error) {
      const text = error instanceof Error ? error.message : "Ошибка отправки свапа";
      setMessage(text);
      showAlert({ type: "error", message: text });
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return (
    <div style={{ display: visState ? "flex" : "none" }}>
      <form onSubmit={execute} style={{ display: "grid", gap: "1rem" }}>
        <fieldset disabled={busy || !loaded} style={{ display: "grid", gap: "1rem", border: 0, padding: 0 }}>
          <label>
            Счёт-источник токенов (SELL)
            <select value={fromBot} onChange={(e) => setFromBot(e.target.value as ESwapBalances)}>
              <option value="MM">MM</option><option value="LIQUID">LIQUID</option>
            </select>
          </label>
          <div>Счёт-получатель токенов (BUY): {toBot}</div>
          <label>
            Порядок ордеров
            <select value={orderMode} onChange={(e) => setOrderMode(e.target.value as SwapOrderMode)}>
              <option value="SELL_BUY">SELL → BUY</option>
              <option value="BUY_SELL">BUY → SELL</option>
              <option value="RANDOM">RANDOM</option>
            </select>
          </label>
          <p>RANDOM выбирает порядок заново при каждом свапе. Счёт-источник всегда продаёт токены. Выбранный порядок сохраняется при запуске.</p>
          <button type="button" onClick={saveMode}>Сохранить порядок</button>
          <label>
            Цена внутри спреда
            <input type="number" step="any" min="0" required value={price} onChange={(e) => setPrice(e.target.value)} />
          </label>
          <label>
            Объём токенов
            <input type="number" step="any" min="0" required value={amount} onChange={(e) => setAmount(e.target.value)} />
          </label>
          <p>Второй ордер отправляется сразу после ответа на первый. Биржа может исполнить часть ордера с другими участниками.</p>
          <button type="submit">Выполнить свап</button>
        </fieldset>
        {busy && <p role="status">Выполняется запрос…</p>}
        {message && <p role="status">{message}</p>}
      </form>
    </div>
  );
};
